import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import parse from 'html-react-parser';
import { Container } from 'react-bootstrap';
import Button from '../../../ui-core/Button/Button';
import Dropdown from '../../../ui-core/Dropdown';
import { TEXT_CONSTANT } from '../../../constants/app';
import { getDisplayType } from '../../../utils/helpers';
import useWindowWidth from '../../../hooks/useWindowWidth';
import useInView from '../../../hooks/useInView';

import './style.css';

const CustomFormatter = ({ section }) => {
  const { description, title } = section;
  const isDesktopScreen = getDisplayType(useWindowWidth()) === 'desktop';
  const [readMore, setReadMore] = useState(!isDesktopScreen);
  const filteredData = isDesktopScreen
    ? description
    : readMore
    ? description.slice(0, 1)
    : description;
  const toggleReadMore = () => setReadMore(prev => !prev);
  const readMoreButtonID = title + '-read-more-button';

  useEffect(() => {
    const ele = document.getElementById(readMoreButtonID);
    if (!isDesktopScreen && ele) {
      ele.addEventListener('click', toggleReadMore);
    }
    return () => {
      if (ele) {
        ele.removeEventListener('click', toggleReadMore);
      }
    };
  }, [isDesktopScreen, readMore]);

  const readMoreButton = `<span id="${readMoreButtonID}" class="color-primary">
      ${readMore ? '... Read More' : ' Read Less'}
    </span>`;
  return (
    <div className="d-flex">
      <ul className="text-start">
        {filteredData.map((item, index, arr) => (
          <span key={`point-${index + 1}`}>
            <li className="mb-2">
              {parse(
                item +
                  (!isDesktopScreen && index === arr.length - 1
                    ? readMoreButton
                    : ''),
              )}
            </li>
          </span>
        ))}
      </ul>
    </div>
  );
};

const FAQSection = props => {
  const Formatter = props.formatter || CustomFormatter;
  const isDesktopScreen = getDisplayType(useWindowWidth()) === 'desktop';
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef([]);
  const headerPositionRef = useRef(null);
  const scrollTopValueRef = useRef(null);
  const wrapperRef = useRef(null);
  const inView = useInView(wrapperRef.current, undefined, false);
  const inViewRef = useRef(inView);
  const headerTopRef = useRef(null);

  function handleScroll(event) {
    if (headerPositionRef.current) {
      const { top } = headerPositionRef.current.getBoundingClientRect() || {};
      const height = headerPositionRef.current.offsetHeight;
      const scrollTop = event.target.scrollTop;

      const index = sectionRef.current.findIndex(ele => {
        const screenHeight = window.innerHeight;
        const { top } = ele.getBoundingClientRect();
        if (top > 0 && top < screenHeight) {
          return true;
        }
        return false;
      });

      if (index !== -1) {
        setActiveIndex(index);
      }

      if (inViewRef.current) {
        headerPositionRef.current.classList.remove('invisible');
      } else {
        headerPositionRef.current.classList.add('invisible');
      }

      if (scrollTopValueRef.current && scrollTopValueRef.current >= scrollTop) {
        scrollTopValueRef.current = null;
        headerPositionRef.current.classList.remove('sticky');
      } else if (top - height <= 0 && !scrollTopValueRef.current) {
        scrollTopValueRef.current = scrollTop;
        headerPositionRef.current.classList.add('sticky');
      }
    }
  }

  useEffect(() => {
    const appElement = document.getElementById('app');
    if (appElement) {
      appElement.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (appElement) {
        appElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    inViewRef.current = inView;
    if (inView) {
      if (headerPositionRef.current && !headerTopRef.current) {
        const appElement = document.getElementById('app');
        headerTopRef.current =
          (appElement?.scrollTop || 0) +
          headerPositionRef.current.getBoundingClientRect().top;
      }
    }
  }, [inView]);

  const addRef = (element, index) => {
    sectionRef.current[index] = element;
  };

  const handleClick = () => {
    if (props.onClick) {
      props.onClick(TEXT_CONSTANT.JOIN_NOW, 'isSignup', 'FAQs section');
    }
  };

  const updateScrollTop = () => {
    if (!scrollTopValueRef.current) {
      scrollTopValueRef.current = headerTopRef.current;
      headerPositionRef.current.classList.add('sticky');
    }
  };

  const handleSectionClick = index => {
    const appElement = document.getElementById('app');
    const element = sectionRef.current[index];
    if (element) {
      if (element?.offsetTop && appElement) {
        const scrollY =
          element.offsetTop -
          (headerPositionRef.current.offsetHeight +
            (scrollTopValueRef.current ? 0 : 60));
        appElement.scroll(0, scrollY);
        updateScrollTop && updateScrollTop();
      } else {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
    setActiveIndex(index);
  };

  return (
    <div className="body-parts-faqs" ref={wrapperRef}>
      {isDesktopScreen ? (
        <div className="section-header how-plan-work" ref={headerPositionRef}>
          {props.faqs.map((section, index) => (
            <span
              className={clsx(
                'section-name',
                index === activeIndex && 'active',
              )}
              key={section.title}
              onClick={() => handleSectionClick(index)}
            >
              {section.title}
            </span>
          ))}
        </div>
      ) : (
        <div className="dropdown-wrapper" ref={headerPositionRef}>
          <Dropdown
            className={'faqs-dropdown'}
            value={activeIndex}
            onChange={event => handleSectionClick(Number(event.target.value))}
            options={props.faqs.map((item, index) => ({
              label: item.title,
              value: index,
            }))}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className="bi bi-chevron-down"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
            />
          </svg>
        </div>
      )}
      <div>
        {props.faqs.map((section, index) => (
          <Container
            className="container-wrapper"
            key={`faq-${index + 1}`}
            ref={ele => addRef(ele, index)}
          >
            <h2 className="text-center">{section.title}</h2>
            <Formatter section={section} />
          </Container>
        ))}
      </div>
      <div className="d-flex">
        <Button
          onClick={handleClick}
          text={TEXT_CONSTANT.JOIN_NOW}
          class="sign-up-button-new d-inline-block mx-auto mb-3"
        />
      </div>
    </div>
  );
};

CustomFormatter.propTypes = {
  section: PropTypes.object,
};

FAQSection.propTypes = {
  faqs: PropTypes.array.isRequired,
  onClick: PropTypes.func,
  formatter: PropTypes.any,
};

export default FAQSection;
